import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';

function clone(items) {
  return JSON.parse(JSON.stringify(items));
}

function normalizeNode(node) {
  return {
    id: node.id ?? node.boardUid,
    label: node.label ?? node.boardNm ?? '',
    description: node.description ?? node.boardDesc ?? '',
    sortOrder: Number(node.sortOrder ?? node.sortOrd ?? 10),
    boardKindCd: node.boardKindCd || 'FOLDER',
    boardTpCd: node.boardTpCd || (node.boardKindCd === 'BOARD' ? 'GENERAL' : null),
    isActive: node.isActive ?? String(node.useYn || 'Y').toUpperCase() !== 'N',
    children: Array.isArray(node.children) ? node.children.map(normalizeNode) : [],
  };
}

function makeNode(label, boardKindCd) {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label,
    description: '',
    sortOrder: 10,
    boardKindCd,
    boardTpCd: boardKindCd === 'BOARD' ? 'GENERAL' : null,
    isActive: true,
    children: [],
  };
}

function findNode(items, id, path = []) {
  for (const node of items) {
    const nextPath = [...path, node];
    if (String(node.id) === String(id)) return { node, path: nextPath };
    if (node.children?.length) {
      const found = findNode(node.children, id, nextPath);
      if (found) return found;
    }
  }
  return null;
}

function findSiblingInfo(items, id) {
  for (let index = 0; index < items.length; index += 1) {
    if (String(items[index].id) === String(id)) {
      return { siblings: items, index };
    }
    if (items[index].children?.length) {
      const found = findSiblingInfo(items[index].children, id);
      if (found) return found;
    }
  }
  return null;
}

function removeNode(items, id) {
  const next = clone(items);
  const remove = (list) => {
    const index = list.findIndex((node) => String(node.id) === String(id));
    if (index >= 0) {
      list.splice(index, 1);
      return true;
    }
    return list.some((node) => node.children?.length && remove(node.children));
  };
  remove(next);
  return next;
}

function updateNode(items, id, updater) {
  const next = clone(items);
  const update = (list) => {
    for (const node of list) {
      if (String(node.id) === String(id)) {
        updater(node);
        return true;
      }
      if (node.children?.length && update(node.children)) return true;
    }
    return false;
  };
  update(next);
  return next;
}

function addTopFolder(items, label) {
  const next = clone(items);
  next.push(makeNode(label, 'FOLDER'));
  next.forEach((node, index) => {
    node.sortOrder = (index + 1) * 10;
  });
  return next;
}

function addBoardToFolder(items, folderId, label) {
  const next = clone(items);
  const update = (list) => {
    for (const node of list) {
      if (String(node.id) === String(folderId)) {
        const children = Array.isArray(node.children) ? node.children : [];
        children.push(makeNode(label, 'BOARD'));
        children.forEach((child, index) => {
          child.sortOrder = (index + 1) * 10;
        });
        node.children = children;
        return true;
      }
    }
    return false;
  };
  update(next);
  return next;
}

function moveNode(items, id, direction) {
  const next = clone(items);
  const found = findSiblingInfo(next, id);
  if (!found) return next;
  const { siblings, index } = found;
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= siblings.length) return next;
  const [node] = siblings.splice(index, 1);
  siblings.splice(targetIndex, 0, node);
  siblings.forEach((item, order) => {
    item.sortOrder = (order + 1) * 10;
  });
  return next;
}

const MODE_EDIT = 'edit';
const MODE_ADD_FOLDER = 'add-folder';
const MODE_ADD_BOARD = 'add-board';

/** 정렬 이동 — 위쪽 삼각 화살표 */
const ARROW_UP_TRIANGLE_ICON = (
  <svg
    className="board-manage-arrow-triangle"
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M12 5 L21 19H3L12 5z" />
  </svg>
);

/** 정렬 이동 — 아래쪽 삼각 화살표 */
const ARROW_DOWN_TRIANGLE_ICON = (
  <svg
    className="board-manage-arrow-triangle"
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M12 19L3 5h18L12 19z" />
  </svg>
);

/** 빈 상태 안내 — 원형 느낌표 */
const EMPTY_STATE_CIRCLE_EXCLAIM_ICON = (
  <span className="board-manage-empty-icon" aria-hidden>
    <svg width={44} height={44} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7v6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="17" r="1.25" fill="currentColor" />
    </svg>
  </span>
);

const CategoryTreeManagePopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [mode, setMode] = useState(MODE_EDIT);
  const [draftFolderName, setDraftFolderName] = useState('');
  const [draftBoardName, setDraftBoardName] = useState('');

  useEffect(() => {
    const nextItems = (config?.treeItems ?? []).map(normalizeNode);
    setItems(nextItems);
    setSelectedId(nextItems[0]?.id ?? null);
    setExpandedIds(new Set(nextItems.map((item) => item.id)));
    setMode(MODE_EDIT);
    setDraftFolderName('');
    setDraftBoardName('');
  }, [config?.treeItems]);

  const originalItems = useMemo(() => (config?.treeItems ?? []).map(normalizeNode), [config?.treeItems]);
  const selected = useMemo(() => findNode(items, selectedId), [items, selectedId]);
  const selectedNode = selected?.node ?? null;
  const selectedPath = selected?.path ?? [];
  const selectedDepth = selectedPath.length > 0 ? selectedPath.length - 1 : -1;
  const selectedTopFolder = selectedNode?.boardKindCd === 'FOLDER' && selectedDepth === 0 ? selectedNode : null;
  const isDirty = useMemo(() => JSON.stringify(items) !== JSON.stringify(originalItems), [items, originalItems]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectNode = useCallback((id) => {
    setSelectedId(id);
    setMode(MODE_EDIT);
    setDraftBoardName('');
    setDraftFolderName('');
  }, []);

  const openAddFolderMode = useCallback(() => {
    setMode(MODE_ADD_FOLDER);
    setDraftFolderName('');
  }, []);

  const openAddBoardMode = useCallback(() => {
    if (!selectedTopFolder) {
      window.alert(t('popup.categoryTreeManage.alertSelectTopFolder'));
      return;
    }
    setMode(MODE_ADD_BOARD);
    setDraftBoardName('');
  }, [selectedTopFolder, t]);

  const handleAddFolder = useCallback(() => {
    const label = String(draftFolderName || '').trim();
    if (!label) {
      window.alert(t('popup.categoryTreeManage.alertEnterFolderName'));
      return;
    }
    const next = addTopFolder(items, label);
    const created = next[next.length - 1];
    setItems(next);
    setSelectedId(created?.id ?? null);
    setExpandedIds((prev) => {
      const nextSet = new Set(prev);
      if (created?.id != null) nextSet.add(created.id);
      return nextSet;
    });
    setMode(MODE_EDIT);
    setDraftFolderName('');
  }, [draftFolderName, items, t]);

  const handleAddBoard = useCallback(() => {
    const label = String(draftBoardName || '').trim();
    if (!label) {
      window.alert(t('popup.categoryTreeManage.alertEnterBoardName'));
      return;
    }
    if (!selectedTopFolder) {
      window.alert(t('popup.categoryTreeManage.alertSelectTopFolder'));
      return;
    }
    const next = addBoardToFolder(items, selectedTopFolder.id, label);
    const parentAfter = findNode(next, selectedTopFolder.id)?.node;
    const created = parentAfter?.children?.[parentAfter.children.length - 1];
    setItems(next);
    setSelectedId(created?.id ?? selectedTopFolder.id);
    setExpandedIds((prev) => {
      const nextSet = new Set(prev);
      nextSet.add(selectedTopFolder.id);
      return nextSet;
    });
    setMode(MODE_EDIT);
    setDraftBoardName('');
  }, [draftBoardName, items, selectedTopFolder, t]);

  const handleDelete = useCallback(() => {
    if (!selectedNode) return;
    if (!window.confirm(t('popup.categoryTreeManage.confirmDelete'))) return;
    const next = removeNode(items, selectedNode.id);
    setItems(next);
    setSelectedId(next[0]?.id ?? null);
    setMode(MODE_EDIT);
  }, [items, selectedNode, t]);

  const handleMove = useCallback((direction) => {
    if (!selectedNode) return;
    setItems((prev) => moveNode(prev, selectedNode.id, direction));
  }, [selectedNode]);

  const handleSave = useCallback(() => {
    onClose(true, { items });
  }, [items, onClose]);

  const handleSelectedChange = useCallback((field, value) => {
    if (!selectedNode || mode !== MODE_EDIT) return;
    setItems((prev) => updateNode(prev, selectedNode.id, (node) => {
      node[field] = value;
    }));
  }, [mode, selectedNode]);

  const renderTreeNodes = (nodes, level = 0) =>
    nodes.map((node) => {
      const hasChildren = node.children?.length > 0;
      const expanded = expandedIds.has(node.id);
      const isSelected = String(selectedId) === String(node.id);
      const isFolder = node.boardKindCd === 'FOLDER';
      const itemId = `manage-tree-${node.id}`;

      return (
        <React.Fragment key={node.id}>
          <div
            className={`tree-menu-item${level > 0 ? ' tree-menu-child-item' : ''}`}
            data-depth={level}
          >
            {hasChildren ? (
              <button
                type="button"
                className="tree-toggle-btn"
                data-toggle-id={itemId}
                aria-expanded={expanded}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpand(node.id);
                }}
              >
                <span className={`tree-toggle-sign${expanded ? ' expanded' : ''}`}>{expanded ? '−' : '+'}</span>
              </button>
            ) : (
              <span className="tree-toggle-spacer" />
            )}
            <div
              className={`tree-menu-content${isSelected ? ' active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => selectNode(node.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectNode(node.id);
                }
              }}
            >
              <span className="tree-menu-label">{node.label}</span>
              <span className={`board-manage-kind-tag${isFolder ? ' is-folder' : ' is-board'}`}>
                {isFolder ? t('popup.categoryTreeManage.folder') : t('popup.categoryTreeManage.board')}
              </span>
              {node.isActive === false ? <span className="board-manage-hidden-badge">{t('popup.categoryTreeManage.hidden')}</span> : null}
            </div>
          </div>
          {hasChildren ? (
            <div className={`tree-menu-children${expanded ? ' expanded' : ''}`} id={itemId}>
              {renderTreeNodes(node.children, level + 1)}
            </div>
          ) : null}
        </React.Fragment>
      );
    });

  return (
    <PopupWrapper
      config={{
        ...config,
        title: config?.title || t('popup.categoryTreeManage.title'),
        width: config?.width ?? 880,
        maxHeight: config?.maxHeight ?? '85vh',
      }}
      onClose={onClose}
      className="popup-category-tree-manage"
      displayType={config?.displayType || 'modal'}
    >
      <div className="board-manage-body">
        <div className="board-manage-layout">
        <section className="board-manage-left">
          <div className="manage-box">
            <div className="manage-box-header">
              <div className="manage-box-title">{t('popup.categoryTreeManage.addTopFolderTitle')}</div>
              <button type="button" className="primary-btn manage-box-add-btn" onClick={openAddFolderMode}>{t('popup.categoryTreeManage.addFolderButton')}</button>
            </div>
          </div>

          <div className="board-manage-tree sidebar-menu sidebar-menu-tree">
            <div className="tree-menu-container">
              {items.length > 0 ? (
                renderTreeNodes(items)
              ) : (
                <div className="empty-box">
                  {EMPTY_STATE_CIRCLE_EXCLAIM_ICON}
                  <span className="empty-box-text">{t('popup.categoryTreeManage.emptyFolder')}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="board-manage-right">
          {mode === MODE_ADD_FOLDER ? (
            <div className="panel-box">
              <div className="panel-title">{t('popup.categoryTreeManage.registerTopFolderTitle')}</div>
              <div className="panel-field-label" id="panel-add-folder-name-label">{t('popup.categoryTreeManage.folderName')}</div>
              <div className="panel-inline-form-row">
                <input
                  type="text"
                  className="panel-inline-input"
                  value={draftFolderName}
                  onChange={(e) => setDraftFolderName(e.target.value)}
                  placeholder={t('popup.categoryTreeManage.folderNamePlaceholder')}
                  aria-labelledby="panel-add-folder-name-label"
                />
                <div className="panel-actions panel-actions--inline">
                  <button type="button" className="outline-btn" onClick={() => setMode(MODE_EDIT)}>{t('popup.common.cancel')}</button>
                  <button type="button" className="primary-btn" onClick={handleAddFolder}>{t('popup.common.create')}</button>
                </div>
              </div>
            </div>
          ) : mode === MODE_ADD_BOARD ? (
            <div className="panel-box">
              <div className="panel-title">{t('popup.categoryTreeManage.registerBoardTitle')}</div>
              <div className="panel-subtitle">
                {t('popup.categoryTreeManage.parentFolder')}: <strong>{selectedTopFolder?.label || '-'}</strong>
              </div>
              <div className="panel-field-label" id="panel-add-board-name-label">{t('popup.categoryTreeManage.boardName')}</div>
              <div className="panel-inline-form-row">
                <input
                  type="text"
                  className="panel-inline-input"
                  value={draftBoardName}
                  onChange={(e) => setDraftBoardName(e.target.value)}
                  placeholder={t('popup.categoryTreeManage.boardNamePlaceholder')}
                  aria-labelledby="panel-add-board-name-label"
                />
                <div className="panel-actions panel-actions--inline">
                  <button type="button" className="outline-btn" onClick={() => setMode(MODE_EDIT)}>{t('popup.common.cancel')}</button>
                  <button type="button" className="secondary-btn" onClick={handleAddBoard}>{t('popup.common.create')}</button>
                </div>
              </div>
            </div>
          ) : selectedNode ? (
            <>
              <div className="selected-summary">
                <div>
                  <div className="selected-title">{selectedNode.boardKindCd === 'FOLDER' ? t('popup.categoryTreeManage.editFolderTitle') : t('popup.categoryTreeManage.editBoardTitle')}</div>
                  <div className="selected-path">{selectedPath.map((item) => item.label).join(' > ')}</div>
                </div>
                <div className="selected-actions">
                  <button type="button" className="arrow-btn" onClick={() => handleMove('up')} aria-label={t('popup.categoryTreeManage.moveUp')}>{ARROW_UP_TRIANGLE_ICON}</button>
                  <button type="button" className="arrow-btn" onClick={() => handleMove('down')} aria-label={t('popup.categoryTreeManage.moveDown')}>{ARROW_DOWN_TRIANGLE_ICON}</button>
                  {selectedTopFolder ? (
                    <button type="button" className="secondary-btn board-manage-add-board-btn" onClick={openAddBoardMode}>{t('popup.categoryTreeManage.addBoardButton')}</button>
                  ) : null}
                  <button type="button" className="danger-btn" onClick={handleDelete}>{t('popup.common.delete')}</button>
                </div>
              </div>

              <div className="form-grid">
                <label>
                  <span>{t('popup.common.name')}</span>
                  <input
                    type="text"
                    value={selectedNode.label || ''}
                    onChange={(e) => handleSelectedChange('label', e.target.value)}
                  />
                </label>

                <label>
                  <span>{t('popup.common.type')}</span>
                  <input
                    type="text"
                    value={selectedNode.boardKindCd === 'FOLDER' ? t('popup.categoryTreeManage.folder') : t('popup.categoryTreeManage.board')}
                    readOnly
                    title={t('popup.categoryTreeManage.readOnlyField')}
                  />
                </label>

                <label>
                  <span>{t('popup.categoryTreeManage.sortOrder')}</span>
                  <input
                    type="number"
                    min="0"
                    value={selectedNode.sortOrder ?? 0}
                    onChange={(e) => handleSelectedChange('sortOrder', Number(e.target.value || 0))}
                  />
                </label>

                <label>
                  <span>{t('popup.categoryTreeManage.boardType')}</span>
                  <input
                    type="text"
                    value={selectedNode.boardKindCd === 'BOARD' ? 'GENERAL' : '-'}
                    readOnly
                    title={t('popup.categoryTreeManage.readOnlyField')}
                  />
                </label>

                <label className="full">
                  <span>{t('popup.common.description')}</span>
                  <textarea
                    value={selectedNode.description || ''}
                    onChange={(e) => handleSelectedChange('description', e.target.value)}
                  />
                </label>

                <label className="checkbox-row full">
                  <input
                    type="checkbox"
                    checked={selectedNode.isActive !== false}
                    onChange={(e) => handleSelectedChange('isActive', e.target.checked)}
                  />
                  <span>{t('popup.categoryTreeManage.isActive')}</span>
                </label>
              </div>
            </>
          ) : (
            <div className="empty-detail">
              {EMPTY_STATE_CIRCLE_EXCLAIM_ICON}
              <span className="empty-detail-text">{t('popup.categoryTreeManage.selectNodeHint')}</span>
            </div>
          )}
        </section>
        </div>
        <div className="board-manage-status-row" role="status" aria-live="polite">
          {isDirty ? t('popup.categoryTreeManage.statusDirty') : t('popup.categoryTreeManage.statusSaved')}
        </div>
      </div>

      <div className="main-popup-footer board-manage-popup-footer">
        <button type="button" className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`} onClick={() => onClose(false)}>
          {t('popup.common.cancel')}
        </button>
        <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={handleSave}>
          {t('popup.common.save')}
        </button>
      </div>

      <style>{`
        .popup-category-tree-manage .board-manage-body {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .popup-category-tree-manage .board-manage-layout {
          display: grid;
          grid-template-columns: 1fr 1.05fr;
          gap: 18px;
          min-height: 400px;
          flex: 1 1 auto;
          align-items: stretch;
        }
        .popup-category-tree-manage .board-manage-left {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .popup-category-tree-manage .board-manage-left .manage-box {
          flex-shrink: 0;
        }
        .popup-category-tree-manage .board-manage-status-row {
          flex-shrink: 0;
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px solid #e2e8f0;
          font-size: 13px;
          color: #64748b;
          line-height: 1.5;
        }
        .popup-category-tree-manage .main-popup-footer.board-manage-popup-footer {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          box-sizing: border-box;
          gap: 12px;
        }
        .popup-category-tree-manage .board-manage-right {
          border-left: 1px solid #e2e8f0;
          padding-left: 18px;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .popup-category-tree-manage .board-manage-right > .empty-detail {
          flex: 1 1 auto;
          min-height: 0;
        }
        .popup-category-tree-manage .manage-box,
        .popup-category-tree-manage .panel-box {
          border: 1px solid transparent;
          background: transparent;
          margin-bottom: 12px;
        }
        .popup-category-tree-manage .manage-box-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .popup-category-tree-manage .manage-box-header .manage-box-title { margin-bottom: 0; flex: 1; min-width: 0; }
        .popup-category-tree-manage .manage-box-add-btn { flex-shrink: 0; }
        .popup-category-tree-manage .manage-box-title,
        .popup-category-tree-manage .selected-title,
        .popup-category-tree-manage .panel-title { font-size: 14px; font-weight: 600; color: #0f172a; margin-bottom: 8px; }
        .popup-category-tree-manage .panel-subtitle { font-size: 13px; color: #64748b; margin-bottom: 12px; }
        .popup-category-tree-manage .panel-field-label {
          font-size: 13px;
          font-weight: 500;
          color: #334155;
          margin-bottom: 6px;
        }
        .popup-category-tree-manage .panel-inline-form-row {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .popup-category-tree-manage .panel-inline-input {
          flex: 1 1 auto;
          min-width: 140px;
          width: auto;
          max-width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 13px;
          box-sizing: border-box;
        }
        .popup-category-tree-manage .panel-actions--inline {
          flex-shrink: 0;
          flex-wrap: nowrap;
        }
        .popup-category-tree-manage .selected-actions,
        .popup-category-tree-manage .panel-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .popup-category-tree-manage .form-grid input,
        .popup-category-tree-manage .form-grid textarea {
          width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; font-size: 13px;
          background-color: #fff;
        }
        .popup-category-tree-manage .form-grid input[readonly],
        .popup-category-tree-manage .form-grid textarea[readonly] {
          background-color: #f1f5f9;
          color: #475569;
          border-color: #e2e8f0;
          cursor: default;
        }
        .popup-category-tree-manage .form-grid input[readonly]:focus,
        .popup-category-tree-manage .form-grid textarea[readonly]:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.35);
          border-color: #cbd5e1;
        }
        .popup-category-tree-manage .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .popup-category-tree-manage .form-grid.single { grid-template-columns: 1fr; }
        .popup-category-tree-manage .form-grid label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #334155; }
        .popup-category-tree-manage .form-grid label.full { grid-column: 1 / -1; }
        .popup-category-tree-manage .form-grid textarea { min-height: 120px; resize: vertical; }
        .popup-category-tree-manage .checkbox-row { flex-direction: row !important; align-items: center; gap: 8px !important; }
        .popup-category-tree-manage .checkbox-row input { width: auto; }
        .popup-category-tree-manage .primary-btn,
        .popup-category-tree-manage .secondary-btn,
        .popup-category-tree-manage .outline-btn,
        .popup-category-tree-manage .danger-btn,
        .popup-category-tree-manage .arrow-btn {
          border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 12px; font-size: 13px; background: #fff; white-space: nowrap;
        }
        .popup-category-tree-manage .primary-btn { background: #2563eb; border-color: #2563eb; color: #fff; }
        .popup-category-tree-manage .secondary-btn { background: #0f766e; border-color: #0f766e; color: #fff; }
        .popup-category-tree-manage .manage-box-add-btn.primary-btn {
          background: #e0f2fe;
          border-color: #bae6fd;
          color: #0369a1;
        }
        .popup-category-tree-manage .manage-box-add-btn.primary-btn:hover {
          background: #bae6fd;
          border-color: #7dd3fc;
          color: #0c4a6e;
        }
        .popup-category-tree-manage .board-manage-add-board-btn.secondary-btn {
          background: #f3e8ff;
          border-color: #e9d5ff;
          color: #6d28d9;
        }
        .popup-category-tree-manage .board-manage-add-board-btn.secondary-btn:hover {
          background: #e9d5ff;
          border-color: #ddd6fe;
          color: #5b21b6;
        }
        .popup-category-tree-manage .danger-btn { background: #fff1f2; border-color: #fecdd3; color: #be123c; }
        .popup-category-tree-manage .arrow-btn {
          width: 38px; padding: 8px 0; display: inline-flex; align-items: center; justify-content: center; font-size: 16px; color: #334155;
        }
        .popup-category-tree-manage .arrow-btn .board-manage-arrow-triangle { display: block; flex-shrink: 0; }
        .popup-category-tree-manage .board-manage-tree {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #fff;
          padding: 12px;
          overflow: auto;
        }
        .popup-category-tree-manage .board-manage-tree .tree-menu-container {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .popup-category-tree-manage .board-manage-tree .tree-menu-content {
          display: flex;
          align-items: center;
          flex-wrap: nowrap;
          gap: 0;
        }
        .popup-category-tree-manage .board-manage-tree .tree-menu-label {
          flex: 1;
          min-width: 0;
          text-align: left;
        }
        .popup-category-tree-manage .board-manage-kind-tag {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 999px;
          flex-shrink: 0;
          margin-left: 8px;
          line-height: 1;
        }
        .popup-category-tree-manage .board-manage-kind-tag.is-folder {
          background: #e0f2fe;
          color: #0369a1;
          border: 1px solid #bae6fd;
        }
        .popup-category-tree-manage .board-manage-kind-tag.is-board {
          background: #f3e8ff;
          color: #6d28d9;
          border: 1px solid #e9d5ff;
        }
        .popup-category-tree-manage .board-manage-tree .tree-menu-content.active .board-manage-kind-tag.is-folder,
        .popup-category-tree-manage .board-manage-tree .tree-menu-content.active .board-manage-kind-tag.is-board {
          background: rgba(255, 255, 255, 0.22);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.45);
        }
        .popup-category-tree-manage .board-manage-hidden-badge {
          font-size: 11px;
          color: #b45309;
          background: #fef3c7;
          border-radius: 999px;
          padding: 2px 8px;
          flex-shrink: 0;
          margin-left: 6px;
        }
        .popup-category-tree-manage .board-manage-tree .tree-menu-content.active .board-manage-hidden-badge {
          background: rgba(255, 255, 255, 0.25);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.4);
        }
        .popup-category-tree-manage .selected-summary {
          display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; margin-bottom: 12px;
        }
        .popup-category-tree-manage .selected-path { font-size: 13px; color: #64748b; line-height: 1.5; }
        .popup-category-tree-manage .board-manage-empty-icon {
          color: #94a3b8;
          margin-bottom: 12px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .popup-category-tree-manage .board-manage-empty-icon svg {
          display: block;
        }
        .popup-category-tree-manage .empty-box,
        .popup-category-tree-manage .empty-detail {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 24px 16px;
          box-sizing: border-box;
          border: 1px dashed #cbd5e1;
          border-radius: 12px;
          color: #64748b;
          font-size: 13px;
          background: #fff;
        }
        .popup-category-tree-manage .empty-box-text,
        .popup-category-tree-manage .empty-detail-text {
          line-height: 1.55;
          max-width: 280px;
        }
        @media (max-width: 960px) {
          .popup-category-tree-manage .board-manage-layout { grid-template-columns: 1fr; }
          .popup-category-tree-manage .board-manage-right { border-left: none; padding-left: 0; border-top: 1px solid #e2e8f0; padding-top: 18px; }
          .popup-category-tree-manage .form-grid { grid-template-columns: 1fr; }
          .popup-category-tree-manage .selected-summary { flex-direction: column; }
        }
      `}</style>
    </PopupWrapper>
  );
};

export default CategoryTreeManagePopup;
